// src/components/ViewBookmarksCarousel.jsx
import React, { useEffect, useState, useRef } from "react";
import "../styles/ViewBookmarksCarousel.css";
import bigLeftArrow from "../images/bigLeftArrow.png";
import bigRightArrow from "../images/bigRightArrow.png";
import playIcon from "../images/playIcon.png";
import delIcon from "../images/delIcon.png";
import {
  fetchUserBookmarks,
  deleteBookMarks,
  fetchBookMarkPageDetails,
} from "../services/AllServices.js";
import { useSearchParams, useNavigate } from "react-router-dom";
import { speakText } from "./utils/speechUtils"; // adjust path if needed

const ViewBookmarksCarousel = () => {
  const [loading, setLoading] = useState(true);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [markedBooks, setMarkedBooks] = useState([]);
  const [selectedBookIdx, setSelectedBookIdx] = useState(0);
  const [searchParams] = useSearchParams();
  const bookid = searchParams.get("bookid");
  const navigate = useNavigate();

  const tileRefs = useRef([]); // to focus tiles programmatically (carousel tiles)
  const sectionRefs = useRef([]); // refs for play buttons (per visible section)

  useEffect(() => {
    let cancelled = false;

    const fetchBookmarks = async () => {
      setLoading(true);
      try {
        const response = await fetchUserBookmarks();
        const books = (response && response.data) || [];
        if (cancelled) return;

        setMarkedBooks(books);

        // Find the index of the book with the given bookid
        const bookIndex = books.findIndex((b) => b._id === bookid);
        if (bookIndex !== -1) {
          const startIndex = Math.max(0, bookIndex - 4);
          setCurrentIdx(startIndex);
          setSelectedBookIdx(bookIndex);
        } else {
          setSelectedBookIdx(0);
          setCurrentIdx(0);
        }

        // Announce page loaded & selected book
        const total = books.length;
        if (total > 0) {
          speakText(`View Bookmarks. ${total} bookmarks.`);
          const announceBook = books[bookIndex] || books[0];
          if (announceBook) speakText(`Selected book: ${announceBook.title}`);
        } else {
          speakText("View Bookmarks. No bookmarks found.");
        }
      } catch (error) {
        console.error("Failed to fetch bookmarks:", error);
        speakText("Failed to fetch bookmarks.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchBookmarks();
    return () => {
      cancelled = true;
    };
  }, [bookid]);

  // Focus the selected tile when selectedBookIdx or markedBooks changes
  useEffect(() => {
    const el = tileRefs.current[selectedBookIdx];
    if (el && typeof el.focus === "function") {
      // timeout ensures browser laid out the element (useful after render/transform)
      setTimeout(() => el.focus({ preventScroll: false }), 0);
    }
  }, [selectedBookIdx, markedBooks]);

  const maxIndex = Math.max(0, markedBooks.length - 10);

  const prevSlide = () => {
    setCurrentIdx((prevIdx) => (prevIdx === 0 ? maxIndex : prevIdx - 1));
  };

  const nextSlide = () => {
    setCurrentIdx((prevIdx) => (prevIdx === maxIndex ? 0 : prevIdx + 1));
  };

  const handleBookSelect = (index) => {
    const selectedBook = markedBooks[index];
    if (selectedBook) {
      // update URL so page can be reloaded/bookmarked with selection
      navigate(`/view-bookmarks?bookid=${selectedBook._id}`);
      setSelectedBookIdx(index);
    }
  };

  /**
   * Delete a bookmark (section)
   * Now accepts bookmarkId and bookID to update state immediately without refresh,
   * and focuses next logical play button after deletion.
   */
  const handleDelete = async (bookmarkId, bookID, sectionIndex) => {
    if (!bookmarkId) return;
    try {
      await deleteBookMarks({ bookmarkId });

      // Compute updated books synchronously so we can decide focus target reliably
      const updatedBooks = markedBooks.map((book) => {
        if (!book || !book._id) return book;
        if (book._id !== bookID) return book;
        const updatedSections = (book.sections || []).filter(
          (s) => s.bookmarkID !== bookmarkId
        );
        return { ...book, sections: updatedSections };
      });

      // Update state with updatedBooks
      setMarkedBooks(updatedBooks);

      // Determine focus target: try to focus the next section's play button,
      // or previous if deleted last, or fallback to the first play button.
      const affectedBook = updatedBooks.find((b) => b._id === bookID);
      const newLen = (affectedBook && affectedBook.sections && affectedBook.sections.length) || 0;
      const desiredIndex = Math.max(0, Math.min(sectionIndex, Math.max(0, newLen - 1)));

      // Slight delay to allow DOM to update
      setTimeout(() => {
        const btn = sectionRefs.current[desiredIndex];
        if (btn && typeof btn.focus === "function") {
          btn.focus();
          // Announce the focused bookmark after focusing
          speakText(`Bookmark ${desiredIndex + 1}`);
        } else {
          // fallback: if no play buttons left, focus the next carousel tile
          const tile = tileRefs.current[selectedBookIdx];
          if (tile && typeof tile.focus === "function") {
            tile.focus();
            speakText("Bookmarks updated");
          }
        }
      }, 80);

      // If deletion impacted section list of the currently selected book,
      // ensure selectedBookIdx remains valid (clamp).
      setSelectedBookIdx((prevIdx) => {
        if (!markedBooks[prevIdx]) return 0;
        const curBook = markedBooks[prevIdx];
        if (curBook._id !== bookID) return prevIdx;
        const remaining = (curBook.sections || []).length - 1;
        if (remaining <= 0) return 0;
        return Math.max(0, Math.min(prevIdx, prevIdx));
      });

      speakText("Bookmark deleted.");
    } catch (error) {
      console.error("Error deleting bookmark:", error);
      speakText("Failed to delete bookmark.");
    }
  };

  const selectedBook = markedBooks[selectedBookIdx];
  const selectedSections = selectedBook?.sections || [];

  // Announce whenever selected book changes (title + count)
  useEffect(() => {
    if (!markedBooks || markedBooks.length === 0) return;
    const book = markedBooks[selectedBookIdx];
    if (book) {
      const sectionsCount = (book.sections && book.sections.length) || 0;
      speakText(`${book.title}. ${sectionsCount} sections.`);
    }
  }, [selectedBookIdx, markedBooks]);

  const handlePlayClick = async (event, section) => {
    event.preventDefault();

    if (!section || !section.bookID || !section.pageID || section.index === undefined) {
      console.error("Missing required section data:", section);
      speakText("Unable to play. Missing data.");
      return;
    }

    speakText("Playing selected section.");
    try {
      const response = await fetchBookMarkPageDetails({
        bookID: section.bookID,
        pageID: section.pageID,
        SectionIndex: section.index,
      });

      if (response && response.data) {
        navigate("/audiobook-player", {
          state: { bookData: response.data },
        });
      } else {
        console.error("Invalid response structure:", response);
        speakText("Sorry, could not load audio.");
      }
    } catch (error) {
      console.error("Error in handlePlayClick:", error);
      speakText("Error while loading audio.");
    }
  };

  if (loading) {
    return (
      <div className="viewbookmarks_carousel__wrap">
        <div className="viewbookmarks__skeleton-loader">
          {[...Array(10)].map((_, index) => (
            <div key={index} className="viewbookmarks__skeleton-item"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="viewbookmarks_carousel__wrap">
      <div className="viewbookmarks_carousel__inner">
        <button
          className="viewbookmarks_carousel__btn"
          onClick={prevSlide}
          aria-label="Previous books"
          onFocus={() => speakText("Previous books")}
          type="button"
        >
          <div className="viewbookmarks_carousel__btn--prev">
            <img
              src={bigLeftArrow}
              alt="Previous"
              className="viewbookmarks_carousel__btn-arrow"
            />
          </div>
        </button>

        <div className="viewbookmarks_carousel__container">
          <div
            className="viewbookmarks_heading"
            tabIndex={0}
            onFocus={() =>
              speakText(`View Bookmarks. You have ${markedBooks.length} bookmarks.`)
            }
          >
            View Bookmarks
          </div>

          <div
            className="viewbookmarks_carousel__slide-list"
            style={{ transform: `translateX(-${currentIdx * (10.3 + 2)}vw)` }}
          >
            {markedBooks.map((book, index) => (
              <button
                key={book._id}
                ref={(el) => (tileRefs.current[index] = el)}
                className={`viewbookmarks_carousel__slide-item ${
                  index === selectedBookIdx ? "active" : ""
                }`}
                aria-label={`Select ${book.title}`}
                onClick={() => handleBookSelect(index)}
                onKeyDown={(e) => {
                  // native button handles Enter/Space, but we keep explicit speak/navigation pre-logic
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleBookSelect(index);
                  }
                }}
                onFocus={() => speakText(book.title)}
                type="button"
              >
                <img
                  src={book.thumbnail}
                  alt={book.title}
                  className="viewbookmarks_carousel__slide-thumbnail"
                />
              </button>
            ))}
          </div>
        </div>

        <button
          className="viewbookmarks_carousel__btn"
          onClick={nextSlide}
          aria-label="Next books"
          onFocus={() => speakText("Next books")}
          type="button"
        >
          <div className="viewbookmarks_carousel__btn--next">
            <img
              src={bigRightArrow}
              alt="Next"
              className="viewbookmarks_carousel__btn-arrow"
            />
          </div>
        </button>
      </div>

      {/* Dynamic content based on selected book */}
      {selectedSections.length > 0 ? (
        <div className="bookmarks_section_container">
          {selectedSections.map((section, index) => {
            const bookmarkNumber = index + 1;
            return (
              <div
                key={section.bookmarkID || index}
                className="section_box"
                style={{
                  background:
                    index % 2 === 0
                      ? "rgba(223, 200, 38, 0.52)"
                      : "rgba(137, 187, 52, 0.52)",
                }}
                onMouseEnter={() =>
                  speakText(`Bookmark ${bookmarkNumber}. ${selectedBook?.title || ""}`)
                }
                // no onFocus on parent — prevents stealing child focus
                tabIndex={-1}
              >
                <div className="play_container">
                  <button
                    ref={(el) => (sectionRefs.current[index] = el)}
                    className="play_img_btn"
                    onClick={(e) => handlePlayClick(e, section)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handlePlayClick(e, section);
                      }
                    }}
                    onFocus={() => speakText(`Bookmark ${bookmarkNumber} play`)}
                    aria-label={`Bookmark ${bookmarkNumber} play`}
                    type="button"
                  >
                    <img src={playIcon} alt="play icon" />
                  </button>
                </div>

                <div className="section_info_container">
                  <div
                    className="section_bookname"
                    tabIndex={0}
                    onFocus={() =>
                      speakText(`Bookmark ${bookmarkNumber} ${selectedBook?.title || "NA"}`)
                    }
                    aria-label={`Bookmark ${bookmarkNumber} title ${selectedBook?.title || ""}`}
                  >
                    {selectedBook?.title || "NA"}
                  </div>
                </div>

                <div className="section_content_container">
                  <p
                    className="section_content"
                    tabIndex={0}
                    onFocus={() => speakText(`Bookmark ${bookmarkNumber} section`)}
                    aria-label={`Bookmark ${bookmarkNumber} section text`}
                  >
                    {section.text || "No content available"}
                  </p>

                  <div className="section_delete">
                    <button
                      className="del_btn"
                      onClick={() =>
                        handleDelete(section.bookmarkID, section.bookID, index)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleDelete(section.bookmarkID, section.bookID, index);
                        }
                      }}
                      onFocus={() => speakText(`Bookmark ${bookmarkNumber} delete`)}
                      aria-label={`Bookmark ${bookmarkNumber} delete`}
                      type="button"
                    >
                      <div className="del_img">
                        <img src={delIcon} alt="delete" />
                      </div>
                      <div className="del_text">Delete</div>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p>No sections available</p>
      )}
    </div>
  );
};

export default ViewBookmarksCarousel;
